import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  approveWorkOrderStockIssue,
  fetchWorkOrderStockIssueDetail,
  rejectWorkOrderStockIssue,
  submitWorkOrderStockIssue,
} from '#/api/momMgmt/workOrder/outbound';
import { approveStockReceipt } from '#/api/imsMgmt/receipts/stockReceipt/approve';
import { fetchStockReceiptDetail } from '#/api/imsMgmt/receipts/stockReceipt/fetchDetail';
import { rejectStockReceipt } from '#/api/imsMgmt/receipts/stockReceipt/reject';
import { submitStockReceipt } from '#/api/imsMgmt/receipts/stockReceipt/submit';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { DocumentStatusBadge } from '#/modules/commonComponents/badge/DocumentStatusBadge';
import { Button } from '#/modules/commonComponents/button';
import {
  AppDialog,
  useConfirmDialog,
} from '#/modules/commonComponents/dialog';
import {
  dialogContentCompactGapClassName,
  dialogWideMaxWidthClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import { DialogFooter } from '#/modules/commonComponents/dialog/DialogPrimitives';
import DetailTable from '#/modules/commonComponents/table/DetailTable';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import DocumentRejectReasonDialog from '#/modules/imsMgmt/receipts/shared/DocumentRejectReasonDialog';
import { DESTRUCTIVE_SOLID_BUTTON_CLASS } from '#/modules/imsMgmt/receipts/shared/destructiveSolidButtonClass';
import {
  canApproveStockReceipt,
  canEditStockReceipt,
  canRejectStockReceipt,
  canSubmitStockReceipt,
} from '#/modules/imsMgmt/receipts/shared/receiptStatus';
import { sumLineQty } from '#/modules/imsMgmt/receipts/stockReceipt/helper/formSerialize';
import {
  canApproveStockIssue,
  canEditStockIssue,
  canRejectStockIssue,
  canSubmitStockIssue,
} from '#/modules/imsMgmt/receipts/stockIssue/helper/issueStatus';
import { useCan } from '#/modules/infra/auth/useCan';
import { getWorkOrderApiErrorMessage } from '#/modules/momMgmt/workOrder/helper/apiError';
import {
  invalidateWorkOrderOutboundQueries,
} from '#/modules/momMgmt/workOrder/outbound/helper/invalidateWorkOrderOutboundQueries';
import { formatDateOrDash } from '#/utils/date';
import { toast } from '#/utils/toast';
import {
  stockIssueDetailQueryKey,
  stockReceiptDetailQueryKey,
  workOrderDetailQueryKey,
} from '#/utils/queryKeys';

import useDetailTable from '#/hooks/table/useDetailTable';

function pickUpdatedAt(detailLike) {
  return detailLike?.record?.updatedAt ?? detailLike?.updatedAt ?? null;
}

function dash(value) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return value;
}

function SummaryCell({ label, value, className }) {
  return (
    <div className={className}>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-0.5 text-sm font-medium wrap-break-word text-gray-900">
        {dash(value)}
      </p>
    </div>
  );
}

SummaryCell.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

function mapIssueLineRows(lines) {
  return (lines ?? []).map((line, index) => ({
    id: line?.id != null && line.id !== '' ? String(line.id) : `issue-line-${index}`,
    stockNo: line?.invStock?.stockNo ?? line?.stockNo ?? null,
    mtrId: line?.mtr?.id ?? null,
    mtrNo: line?.mtr?.mtrNo ?? '—',
    name: line?.mtr?.name ?? '—',
    qty: line?.qty ?? '—',
    unit: line?.unit?.name ?? line?.unit?.code ?? '—',
  }));
}

function mapReceiptLineRows(lines) {
  const rows = [];
  (lines ?? []).forEach((line, lineIndex) => {
    const mtr = line?.mtr ?? line?.material;
    const mtrNo = mtr?.mtrNo ?? mtr?.materialNo ?? '—';
    const name = mtr?.name ?? '—';
    const mtrId = mtr?.id ?? null;
    const packages = line?.packages ?? [];
    if (packages.length === 0) {
      rows.push({
        id: `receipt-line-${lineIndex}`,
        stockNo: null,
        mtrId,
        mtrNo,
        name,
        qty: '—',
        unit: '—',
      });
      return;
    }
    packages.forEach((pkg, pkgIndex) => {
      rows.push({
        id: `receipt-line-${lineIndex}-pkg-${pkgIndex}`,
        stockNo: pkg?.invStock?.stockNo ?? pkg?.stockNo ?? null,
        mtrId,
        mtrNo,
        name,
        qty: pkg?.qty ?? pkg?.quantity ?? '—',
        unit: pkg?.unit?.name ?? pkg?.unit?.code ?? '—',
      });
    });
  });
  return rows;
}

/** 入庫預覽：加總本單 lines[].packages[].qty（可解析數字，否則 0） */
function sumReceiptPackagesQty(lines) {
  return (lines ?? []).reduce((sum, line) => sum + sumLineQty(line), 0);
}

function mtrPathOf(row) {
  return row?.mtrId ? ROUTES.mtrDetail(row.mtrId) : null;
}

function buildLineColumns({ includeStockNo = false } = {}) {
  const columns = [];

  if (includeStockNo) {
    columns.push({
      id: 'stockNo',
      header: '庫存編號',
      accessorKey: 'stockNo',
      size: 150,
      minSize: 120,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
      cell: ({ row }) => {
        const stockNo = row.original.stockNo;
        if (!stockNo) {
          return (
            <span className="text-muted-foreground">尚未建立庫存編號</span>
          );
        }
        return (
          <span className="font-mono">{stockNo}</span>
        );
      },
    });
  }

  columns.push(
    {
      id: 'mtrNo',
      header: '料號',
      accessorKey: 'mtrNo',
      size: 140,
      minSize: 100,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip
          text={row.original.mtrNo}
          detailPath={mtrPathOf(row.original)}
        />
      ),
    },
    {
      id: 'name',
      header: '名稱',
      accessorKey: 'name',
      size: 200,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip
          text={row.original.name}
          detailPath={mtrPathOf(row.original)}
        />
      ),
    },
    {
      id: 'qty',
      header: '數量',
      accessorKey: 'qty',
      size: 90,
      enableSorting: false,
    },
    {
      id: 'unit',
      header: '單位',
      accessorKey: 'unit',
      size: 90,
      enableSorting: false,
    },
  );

  return columns;
}

/**
 * 工單出庫／入庫歷史預覽：自寫 header／body／footer（不嵌 MainInfoCard）。
 */
const WorkOrderRelatedDocPreviewDialog = ({
  open,
  onOpenChange,
  variant,
  workOrderId,
  docId,
  onMutated,
}) => {
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const [rejectOpen, setRejectOpen] = useState(false);
  const canCreate = useCan(PERM.WORK_ORDER_CREATE);
  const canUpdate = useCan(PERM.WORK_ORDER_UPDATE);
  const canReview = useCan(PERM.WORK_ORDER_REVIEW);

  const isIssue = variant === 'stockIssue';
  const detailQueryKey = isIssue
    ? stockIssueDetailQueryKey(docId)
    : stockReceiptDetailQueryKey(docId);

  const detailQuery = useQuery({
    queryKey: detailQueryKey,
    queryFn: async () => {
      if (isIssue) {
        return fetchWorkOrderStockIssueDetail(workOrderId, docId);
      }
      return fetchStockReceiptDetail(docId);
    },
    enabled: Boolean(open && docId && (isIssue ? workOrderId : true)),
  });

  const detail = detailQuery.data?.data;
  const status = detail?.status;
  const docNo = isIssue
    ? (detail?.issueNo ?? detail?.documentNo ?? '—')
    : (detail?.receiptNo ?? detail?.documentNo ?? '—');
  const detailPath = isIssue
    ? (docId ? `${ROUTES.stockIssueDetail(docId)}?workOrderId=${encodeURIComponent(workOrderId)}` : null)
    : (docId ? ROUTES.stockReceiptDetail(docId) : null);

  const description = docNo && docNo !== '—'
    ? (
      <div className="flex flex-wrap items-center gap-2">
        <TruncatedTextWithTooltip
          text={String(docNo)}
          detailPath={detailPath}
          tooltipContent="雙擊開啟單據（新分頁）"
          className="text-sm font-medium"
        />
        {status ? <DocumentStatusBadge status={status} size="sm" /> : '—'}
      </div>
    )
    : undefined;

  const canSubmit = canCreate && (isIssue
    ? canSubmitStockIssue(status)
    : canSubmitStockReceipt(status));
  const canApprove = canReview && (isIssue
    ? canApproveStockIssue(status)
    : canApproveStockReceipt(status));
  const canReject = canReview && (isIssue
    ? canRejectStockIssue(status)
    : canRejectStockReceipt(status));
  const canEditDraft = canUpdate && (isIssue
    ? canEditStockIssue(status)
    : canEditStockReceipt(status));
  const editPath = isIssue
    ? ROUTES.workOrderOutboundEdit(workOrderId, docId)
    : ROUTES.workOrderInboundEdit(workOrderId, docId);

  const lineRows = useMemo(() => {
    if (!detail) {
      return [];
    }
    return isIssue
      ? mapIssueLineRows(detail.lines)
      : mapReceiptLineRows(detail.lines);
  }, [detail, isIssue]);

  const lineColumns = useMemo(
    () => buildLineColumns({ includeStockNo: true }),
    [],
  );

  const detailTableState = useDetailTable(lineRows, lineColumns, {
    enableExpanding: false,
    getRowId: (row) => String(row.id),
  });

  const mutation = useMutation({
    mutationFn: async ({ action, rejectReason }) => {
      if (!docId || !workOrderId) {
        throw new Error('缺少單據或工單 id');
      }
      const updatedAt = pickUpdatedAt(detail);
      if (!updatedAt) {
        throw new Error('缺少更新時間，請重新載入後再試');
      }

      if (isIssue) {
        if (action === 'submit') {
          return submitWorkOrderStockIssue(workOrderId, docId, { updatedAt });
        }
        if (action === 'approve') {
          return approveWorkOrderStockIssue(workOrderId, docId, { updatedAt });
        }
        if (action === 'reject') {
          return rejectWorkOrderStockIssue(workOrderId, docId, {
            updatedAt,
            rejectReason,
          });
        }
      } else {
        if (action === 'submit') {
          return submitStockReceipt(docId, { updatedAt });
        }
        if (action === 'approve') {
          return approveStockReceipt(docId, { updatedAt });
        }
        if (action === 'reject') {
          return rejectStockReceipt(docId, { updatedAt, rejectReason });
        }
      }

      throw new Error(`Unknown action: ${action}`);
    },
    onSuccess: async (result, variables) => {
      toast.success(result?.message || '操作成功');
      if (variables.action === 'reject') {
        setRejectOpen(false);
      }
      if (isIssue && workOrderId) {
        await invalidateWorkOrderOutboundQueries(queryClient, workOrderId);
      } else {
        await queryClient.invalidateQueries({
          queryKey: workOrderDetailQueryKey(workOrderId),
        });
      }
      await queryClient.invalidateQueries({ queryKey: detailQueryKey });
      await onMutated?.();
    },
    onError: (error) => {
      toast.error(getWorkOrderApiErrorMessage(error));
    },
  });

  const isPending = mutation.isPending;

  async function handleSubmit() {
    const ok = await confirm({
      title: isIssue ? '提交出庫單' : '提交入庫單',
      message: '提交後將進入待核准，確定提交？',
      confirmText: '提交',
    });
    if (!ok) {
      return;
    }
    mutation.mutate({ action: 'submit' });
  }

  async function handleApprove() {
    const ok = await confirm({
      title: isIssue ? '核准出庫單' : '核准入庫單',
      message: isIssue
        ? '核准後將扣減庫存，確定核准？'
        : '核准後將入帳庫存，確定核准？',
      confirmText: '核准',
    });
    if (!ok) {
      return;
    }
    mutation.mutate({ action: 'approve' });
  }

  function handleRejectConfirm(rejectReason) {
    mutation.mutate({ action: 'reject', rejectReason });
  }

  function handleClose() {
    if (isPending) {
      return;
    }
    onOpenChange(false);
  }

  const summaryDate = isIssue
    ? formatDateOrDash(detail?.issueDate)
    : formatDateOrDash(detail?.receiptDate);
  const summaryCategoryOrWarehouse = isIssue
    ? ([detail?.category?.code, detail?.category?.name].filter(Boolean).join(' ')
      || '—')
    : (detail?.warehouse?.name ?? detail?.warehouse?.displayName ?? '—');
  const receiptInboundTotalQty = !isIssue && detail
    ? sumReceiptPackagesQty(detail.lines)
    : null;

  const footer = (
    <DialogFooter>
      <Button
        type="button"
        variant="outline"
        disabled={isPending}
        onClick={handleClose}
      >
        關閉
      </Button>
      {canEditDraft && workOrderId && docId ? (
        <Button type="button" variant="outline" asChild>
          <Link to={editPath}>
            編輯
          </Link>
        </Button>
      ) : null}
      {canReject ? (
        <Button
          type="button"
          variant="destructive"
          className={cn(DESTRUCTIVE_SOLID_BUTTON_CLASS)}
          disabled={isPending || detailQuery.isLoading}
          onClick={() => setRejectOpen(true)}
        >
          退回
        </Button>
      ) : null}
      {canApprove ? (
        <Button
          type="button"
          disabled={isPending || detailQuery.isLoading}
          onClick={handleApprove}
        >
          核准
        </Button>
      ) : null}
      {canSubmit ? (
        <Button
          type="button"
          disabled={isPending || detailQuery.isLoading}
          onClick={handleSubmit}
        >
          提交
        </Button>
      ) : null}
    </DialogFooter>
  );

  return (
    <>
      {ConfirmDialogHost}
      <DocumentRejectReasonDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        title={isIssue ? '退回出庫單' : '退回入庫單'}
        description="退回後將恢復為草稿，確定退回？"
        confirmText="退回"
        isPending={isPending}
        onConfirm={handleRejectConfirm}
      />
      <AppDialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && isPending) {
            return;
          }
          onOpenChange(nextOpen);
        }}
        title={isIssue ? '出庫單預覽' : '入庫單預覽'}
        description={description}
        headerVariant="primary"
        showCloseButton
        maxWidthClassName={dialogWideMaxWidthClassName}
        contentClassName={dialogContentCompactGapClassName}
        closeOnOutsideClick={!isPending}
        closeOnEscape={!isPending}
        footer={footer}
      >
        {detailQuery.isLoading ? (
          <p className="py-6 text-sm text-muted-foreground">載入中…</p>
        ) : null}
        {detailQuery.isError ? (
          <p className="py-6 text-sm text-destructive">
            {getWorkOrderApiErrorMessage(detailQuery.error)}
          </p>
        ) : null}
        {detail && !detailQuery.isLoading ? (
          <div className="space-y-4">
            <div
              className={cn(
                'grid grid-cols-1 gap-x-4 gap-y-3',
                'sm:grid-cols-2',
              )}
            >
              <SummaryCell label="日期" value={summaryDate} />
              <SummaryCell
                label={isIssue ? '類別' : '倉庫'}
                value={summaryCategoryOrWarehouse}
              />
              {!isIssue ? (
                <SummaryCell
                  label="已入庫總量"
                  value={receiptInboundTotalQty}
                  className="col-span-1 sm:col-span-2"
                />
              ) : null}
              <SummaryCell label="事由" value={detail.issueReason} />
              <SummaryCell label="備註" value={detail.remark} />
            </div>
            <div>
              <p className="mb-2 text-xs font-medium text-gray-500">明細</p>
              {lineRows.length === 0 ? (
                <p className="text-sm text-muted-foreground">尚無明細</p>
              ) : (
                <DetailTable
                  state={detailTableState}
                  containerClassName="max-h-72 max-w-full overflow-auto"
                  tableClassName="detail-table--flat-lines"
                />
              )}
            </div>
          </div>
        ) : null}
      </AppDialog>
    </>
  );
};

WorkOrderRelatedDocPreviewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['stockIssue', 'stockReceipt']).isRequired,
  workOrderId: PropTypes.string.isRequired,
  docId: PropTypes.string.isRequired,
  onMutated: PropTypes.func,
};

export default WorkOrderRelatedDocPreviewDialog;
